import {
  CONSISTENCY_STATUS_LABELS,
  type ConsistencyStatusCode,
  REPORT_SALES_UNIT_LABELS,
} from "@station/contracts";
import {
  Alert,
  Checkbox,
  EmptyState,
  Label,
  MoneyText,
  PageHeader,
  Select,
  SkeletonRows,
  StatusBadge,
} from "@station/ui";
import { createFileRoute, Link } from "@tanstack/react-router";
import { type ReactElement, type ReactNode, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { useConsistency, useProfitability } from "../../../features/reports/queries.js";
import { formatPercentValue, rangeError, rangeLimitFor } from "../../../features/reports/report-values.js";
import {
  ReportFilterBar,
  ReportsGate,
  reportSearchShape,
  SectionCard,
  StatTile,
  TableScroll,
  useReportFilter,
  useScopeOptions,
} from "../../../features/reports/shared.js";
import { errorMessage } from "../../../lib/errors.js";
import { useDocumentTitle } from "../../../lib/hooks.js";

const GROUPINGS = ["STATION_BUSINESS_UNIT", "STATION", "BUSINESS_UNIT"] as const;
type Grouping = (typeof GROUPINGS)[number];

const searchSchema = z.object(reportSearchShape);

export const Route = createFileRoute("/_app/raporlar/karlilik")({
  validateSearch: (search) => searchSchema.parse(search),
  component: ProfitabilityRoute,
});

const STATUS_TONES: Record<ConsistencyStatusCode, "success" | "warning" | "danger" | "info"> = {
  MATCH: "success",
  UNPOSTED: "warning",
  LEDGER_ONLY: "info",
  DIFFERENCE: "danger",
};

/**
 * Kârlılık (Prompt 22, ADR 0022 §6, §10): kârlılık HER ZAMAN ANA DEFTERDEN (istasyon × iş birimi boyutlu gelir / gider
 * fişleri); yanında operasyonel özet ↔ defter tutarlılık paneli (KDV hariç operasyonel ciro ile defterdeki gelir, fark ve
 * olası neden — çoğunlukla fişlenmemiş gün sonu).
 */
function ProfitabilityRoute(): ReactElement {
  const { t } = useTranslation();
  useDocumentTitle(t("menu.reportsProfitability"));
  return (
    <>
      <PageHeader
        title={t("menu.reportsProfitability")}
        description={t("reports.profitability.description")}
      />
      <ReportsGate>
        <Profitability />
      </ReportsGate>
    </>
  );
}

function Th({ children, align = "left" }: { children: ReactNode; align?: "left" | "right" }): ReactElement {
  return (
    <th scope="col" className={`px-3 py-2 font-medium ${align === "right" ? "text-right" : "text-left"}`}>
      {children}
    </th>
  );
}

function Profitability(): ReactElement {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const [filter, setFilter, explicit] = useReportFilter(search, "THIS_MONTH");
  const options = useScopeOptions();
  const [groupBy, setGroupBy] = useState<Grouping>("STATION_BUSINESS_UNIT");
  const [includeDrafts, setIncludeDrafts] = useState(false);
  const groupId = useId();
  const draftsId = useId();
  const companyId = filter.companyId ?? options.companies[0]?.id ?? null;
  const state = { ...filter, companyId };
  const maxDays = rangeLimitFor("PROFITABILITY");
  const valid = rangeError(state.range, maxDays) === null && companyId !== null;
  const profitability = useProfitability(state.range, companyId, { groupBy, includeDrafts }, valid);
  const consistency = useConsistency(state.range, companyId, valid);
  const unitLabel = (unit: string | null) =>
    unit
      ? ((REPORT_SALES_UNIT_LABELS as Record<string, string>)[unit] ?? unit)
      : t("reports.profitability.noUnit");

  return (
    <div className="grid gap-6">
      <ReportFilterBar
        reportKey="PROFITABILITY"
        state={state}
        onChange={setFilter}
        stations="none"
        companyRequired
        maxDays={maxDays}
        explicit={explicit}
        exportExtra={{ groupBy, includeDrafts }}
        extra={
          <>
            <div className="grid gap-1.5">
              <Label htmlFor={groupId}>{t("reports.profitability.groupBy")}</Label>
              <Select
                id={groupId}
                className="w-56"
                value={groupBy}
                onChange={(event) => setGroupBy(event.target.value as Grouping)}
              >
                {GROUPINGS.map((key) => (
                  <option key={key} value={key}>
                    {t(`reports.profitability.groupings.${key}`)}
                  </option>
                ))}
              </Select>
            </div>
            <label htmlFor={draftsId} className="flex h-10 items-center gap-2 text-sm">
              <Checkbox
                id={draftsId}
                checked={includeDrafts}
                onChange={(event) => setIncludeDrafts(event.target.checked)}
              />
              {t("reports.profitability.includeDrafts")}
            </label>
          </>
        }
      />

      <SectionCard
        title={t("reports.profitability.ledgerTitle")}
        description={t("reports.profitability.ledgerDescription")}
        actions={
          profitability.data?.includesDrafts ? (
            <StatusBadge tone="warning">{t("reports.profitability.withDrafts")}</StatusBadge>
          ) : null
        }
      >
        {profitability.error ? <Alert tone="danger">{errorMessage(profitability.error)}</Alert> : null}
        {!profitability.data ? (
          profitability.isPending && valid ? (
            <SkeletonRows rows={5} />
          ) : null
        ) : profitability.data.rows.length === 0 ? (
          <EmptyState
            title={t("reports.profitability.emptyTitle")}
            description={t("reports.profitability.emptyDescription")}
          />
        ) : (
          <>
            <div className="grid gap-3 sm:grid-cols-3">
              <StatTile
                label={t("reports.profitability.revenue")}
                value={<MoneyText value={profitability.data.totals.revenue} />}
              />
              <StatTile
                label={t("reports.profitability.expense")}
                value={<MoneyText value={profitability.data.totals.expense} />}
              />
              <StatTile
                label={t("reports.profitability.net")}
                value={<MoneyText value={profitability.data.totals.net} colorize />}
              />
            </div>
            <TableScroll label={t("reports.profitability.ledgerTitle")}>
              <table className="w-full min-w-[640px] text-sm">
                <caption className="sr-only">{t("reports.profitability.ledgerTitle")}</caption>
                <thead className="bg-muted/50">
                  <tr>
                    {groupBy !== "BUSINESS_UNIT" ? <Th>{t("reports.columns.station")}</Th> : null}
                    {groupBy !== "STATION" ? <Th>{t("reports.columns.unit")}</Th> : null}
                    <Th align="right">{t("reports.profitability.revenue")}</Th>
                    <Th align="right">{t("reports.profitability.expense")}</Th>
                    <Th align="right">{t("reports.profitability.net")}</Th>
                  </tr>
                </thead>
                <tbody>
                  {profitability.data.rows.map((row, index) => (
                    <tr
                      key={`${row.stationId ?? "-"}:${row.businessUnit ?? "-"}:${index}`}
                      className="border-t"
                    >
                      {groupBy !== "BUSINESS_UNIT" ? (
                        <td className="px-3 py-2">
                          {row.stationName ?? t("reports.profitability.noStation")}
                        </td>
                      ) : null}
                      {groupBy !== "STATION" ? (
                        <td className="px-3 py-2">{unitLabel(row.businessUnit)}</td>
                      ) : null}
                      <td className="px-3 py-2 text-right">
                        <MoneyText value={row.revenue} />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <MoneyText value={row.expense} />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <MoneyText value={row.net} colorize />
                      </td>
                    </tr>
                  ))}
                  <tr className="border-t-2 bg-muted/40 font-semibold">
                    <th
                      scope="row"
                      className="px-3 py-2 text-left"
                      colSpan={groupBy === "STATION_BUSINESS_UNIT" ? 2 : 1}
                    >
                      {t("reports.columns.total")}
                    </th>
                    <td className="px-3 py-2 text-right">
                      <MoneyText value={profitability.data.totals.revenue} />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <MoneyText value={profitability.data.totals.expense} />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <MoneyText value={profitability.data.totals.net} colorize />
                    </td>
                  </tr>
                </tbody>
              </table>
            </TableScroll>
          </>
        )}
      </SectionCard>

      <SectionCard title={t("reports.consistency.title")} description={t("reports.consistency.description")}>
        {consistency.error ? <Alert tone="danger">{errorMessage(consistency.error)}</Alert> : null}
        {!consistency.data ? (
          consistency.isPending && valid ? (
            <SkeletonRows rows={5} />
          ) : null
        ) : (
          <>
            {consistency.data.vatRateMissing ? (
              <Alert tone="warning">{t("reports.consistency.vatMissing")}</Alert>
            ) : null}
            <div className="grid gap-3 sm:grid-cols-3">
              <StatTile
                label={t("reports.consistency.operational")}
                value={<MoneyText value={consistency.data.totals.operationalNet} />}
              />
              <StatTile
                label={t("reports.consistency.ledger")}
                value={<MoneyText value={consistency.data.totals.ledgerRevenue} />}
              />
              <StatTile
                label={t("reports.consistency.difference")}
                value={<MoneyText value={consistency.data.totals.difference} colorize />}
              />
            </div>
            <TableScroll label={t("reports.consistency.title")}>
              <table className="w-full min-w-[900px] text-sm">
                <caption className="sr-only">{t("reports.consistency.title")}</caption>
                <thead className="bg-muted/50">
                  <tr>
                    <Th>{t("reports.columns.station")}</Th>
                    <Th>{t("reports.columns.unit")}</Th>
                    <Th align="right">{t("reports.consistency.operational")}</Th>
                    <Th align="right">{t("reports.consistency.ledger")}</Th>
                    <Th align="right">{t("reports.consistency.difference")}</Th>
                    <Th align="right">{t("reports.consistency.differencePercent")}</Th>
                    <Th>{t("reports.consistency.status")}</Th>
                    <Th align="right">{t("reports.consistency.unpostedDays")}</Th>
                  </tr>
                </thead>
                <tbody>
                  {consistency.data.rows.map((row, index) => (
                    <tr key={`${row.stationId ?? "-"}:${row.unit ?? "-"}:${index}`} className="border-t">
                      <td className="px-3 py-2">{row.stationName ?? t("reports.profitability.noStation")}</td>
                      <td className="px-3 py-2">{unitLabel(row.unit)}</td>
                      <td className="px-3 py-2 text-right">
                        <MoneyText value={row.operationalNet} />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <MoneyText value={row.ledgerRevenue} />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <MoneyText value={row.difference} colorize />
                      </td>
                      <td className="tabular px-3 py-2 text-right">
                        {formatPercentValue(row.differencePercent)}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2">
                        <StatusBadge tone={STATUS_TONES[row.status]}>
                          {CONSISTENCY_STATUS_LABELS[row.status]}
                        </StatusBadge>
                      </td>
                      <td className="tabular px-3 py-2 text-right">{row.unpostedDays}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableScroll>
            {consistency.data.notes.length > 0 ? (
              <ul className="grid list-disc gap-1 pl-5 text-xs text-muted-foreground">
                {consistency.data.notes.map((note) => (
                  <li key={note}>{note}</li>
                ))}
              </ul>
            ) : null}
            <p className="text-xs text-muted-foreground">
              {t("reports.consistency.hint")}{" "}
              <Link
                to="/vardiya/gun-sonu"
                className="font-medium text-primary underline-offset-4 hover:underline"
              >
                {t("reports.consistency.dayEndLink")}
              </Link>
            </p>
          </>
        )}
      </SectionCard>
    </div>
  );
}
